'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { toast } from 'react-toastify';
import { Tooltip, Burger } from '@mantine/core';

import { Button } from '@/elements/button/button.component';
import { SubscribeButton } from '@/elements/subscribe-button/subscribe-button.component';
import { MobileMenu } from '@/elements/mobile-menu/mobile-menu.component';
import {
  HeaderWrapper,
  Container,
  Section,
  Icon,
  MenuWrap,
  LinksMenu,
  Mobile,
  SubIcon,
} from './header.elements';
import { PwaInstallButton } from './pwa-install-button.component';
import { Banner } from '../banner/banner.component';

import { logout } from '@/actions/auth.actions';
import { useAuth } from '@/providers/auth.provider';
import { useBanner } from '@/providers/banner.provider';
import { scrollToElement } from '@/utils/functions.utils';
import { handleErrors } from '@/helpers/handle-errors.helper';

import { IconCalendarMonthFilled } from '@tabler/icons-react';
import izvrsnostLogo from '@/assets/img/izvrsnost-logo.png';
import { colors } from '@/styles/theme';

const Header = ({ banners = [] }) => {
  const pathname = usePathname();
  const [showLeftMenu, setShowLeftMenu] = useState(false);
  const [showRightMenu, setShowRightMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [logoIsVisible, setLogoIsVisible] = useState(pathname !== '/');
  const [calendarEventsCount, setCalendarEventsCount] = useState(0);
  const [isMounted, setIsMounted] = useState(false);

  // lastScrollY is read inside a scroll handler that fires many times per
  // second. Keeping it in state would re-run the scroll effect on every tick
  // (removeEventListener/addEventListener churn), which is brutal on iOS
  // Safari and can crash the tab on Apple devices during sustained scrolling.
  // A ref avoids the re-render cycle entirely.
  const lastScrollYRef = useRef(0);

  const headerRef = useRef(null);
  const menuLeftRef = useRef(null);
  const menuRightRef = useRef(null);
  const menuMobileRef = useRef(null);
  const { setHeaderHeight } = useBanner();
  const router = useRouter();
  const { user, setUser, isLoading } = useAuth();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const heroLogo = document.getElementById('hero-logo');

    if (!heroLogo) {
      setLogoIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setLogoIsVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(heroLogo);

    return () => {
      observer.disconnect();
    };
  }, [pathname]);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      const lastScrollY = lastScrollYRef.current;

      // Show header at the top of the page
      if (currentScrollY < 50) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down
        setIsVisible(false);
      } else {
        // Scrolling up
        setIsVisible(true);
      }

      lastScrollYRef.current = currentScrollY;
    };

    const handleScroll = () => {
      setShowLeftMenu(false);
      setShowRightMenu(false);
    };

    // passive: true avoids blocking the main thread on iOS Safari and
    // prevents the scroll jank that previously crashed Apple devices.
    window.addEventListener('scroll', controlHeader, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', controlHeader);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Add request debouncing and caching for better performance
    const controller = new AbortController();

    const fetchWebinars = async () => {
      try {
        const res = await fetch('/api/v1/getUpcomingWebinars', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            period: 'today',
          }),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error('Failed to fetch webinars');

        const data = await res.json();
        if (data.webinars && data.webinars.length > 0) {
          setCalendarEventsCount(data.webinars.length);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleErrors(error, { component: 'header', action: 'fetch upcoming webinars' });
        }
      }
    };

    fetchWebinars();

    return () => {
      controller.abort();
    };
  }, []);

  const handleLogIn = () => {
    // Use window.location.search to avoid useSearchParams which requires Suspense
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const fullPath = pathname + search;
    localStorage.setItem('referrer', fullPath);
    router.push('/prijava');
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      toast.info('Odjava uspješna. Vidimo se!', { theme: 'colored' });
      router.push('/');
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuLeftRef.current && !menuLeftRef.current.contains(e.target)) {
        setShowLeftMenu(false);
      }

      if (menuRightRef.current && !menuRightRef.current.contains(e.target)) {
        setShowRightMenu(false);
      }

      if (menuMobileRef.current && !menuMobileRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuLeftRef, menuRightRef, menuMobileRef]);

  // Measure header height and update context for Main margin
  useEffect(() => {
    const measureHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    measureHeight();

    // Re-measure on resize
    window.addEventListener('resize', measureHeight);

    // Use ResizeObserver to detect header size changes (e.g., banner appearing)
    const resizeObserver = new ResizeObserver(measureHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener('resize', measureHeight);
      resizeObserver.disconnect();
    };
  }, [setHeaderHeight]);

  return (
    <HeaderWrapper ref={headerRef} $isVisible={isVisible}>
      <Banner banners={banners} />
      <Container>
        <Section>
          {logoIsVisible ? (
            <Link href="/">
              <Image src={izvrsnostLogo} height={40} alt="Inkubator izvrsnosti logo" />
            </Link>
          ) : null}
          <Link href="/edukacije">
            <Button naked>Edukacije</Button>
          </Link>
          <Link href="/predavaci">
            <Button naked>Predavači</Button>
          </Link>
          <Link href="/blog">
            <Button naked>Blog</Button>
          </Link>
          <Link href="/daruj-edukaciju">
            <Button naked>Daruj edukaciju</Button>
          </Link>
          <MenuWrap ref={menuLeftRef}>
            <Button
              naked
              icon={showLeftMenu ? 'chevron-up' : 'chevron-down'}
              onClick={() => setShowLeftMenu(!showLeftMenu)}
            >
              Više
            </Button>
            {showLeftMenu ? (
              <LinksMenu>
                <Link href="/kalendar" onClick={() => setShowLeftMenu(false)}>
                  <Button naked justify="flex-start" width={'154px'}>
                    Kalendar
                  </Button>
                </Link>
                <Link href="/novosti" onClick={() => setShowLeftMenu(false)}>
                  <Button naked justify="flex-start" width={'154px'}>
                    Novosti
                  </Button>
                </Link>
                <Link href="/o-nama" onClick={() => setShowLeftMenu(false)}>
                  <Button naked justify="flex-start" width={'154px'}>
                    O nama
                  </Button>
                </Link>
                <div>
                  <Button
                    naked
                    justify="flex-start"
                    width={'154px'}
                    onClick={() => {
                      scrollToElement('contact-us');
                      setShowLeftMenu(false);
                    }}
                  >
                    Kontakt
                  </Button>
                </div>
              </LinksMenu>
            ) : null}
          </MenuWrap>
        </Section>

        <Section>
          {isMounted &&
          !isLoading &&
          !['/pretplata', '/prijava-na-edukaciju'].includes(pathname) &&
          (!user || (user.roles?.includes('student') && !user.subscriptionStartedAt)) ? (
            <Link href="/pretplata">
              <SubscribeButton>Pretplati se</SubscribeButton>
            </Link>
          ) : null}

          {isMounted && !isLoading && user ? (
            pathname !== '/moj-profil' ? (
              <Link href="/moj-profil">
                <Button>Moj profil</Button>
              </Link>
            ) : null
          ) : isMounted && !isLoading && !['/prijava', '/registracija'].includes(pathname) ? (
            <Button onClick={handleLogIn}>Prijavi se</Button>
          ) : null}

          {pathname !== '/kalendar' ? (
            <Link href="/kalendar">
              <Tooltip
                label="Pogledaj raspored edukacija"
                events={{ hover: true, focus: true }}
                withArrow
                transitionProps={{ duration: 200 }}
                zIndex={100}
              >
                <Icon>
                  <IconCalendarMonthFilled size={32} stroke={1} color={colors.brown} />
                  {calendarEventsCount > 0 && <SubIcon>{calendarEventsCount}</SubIcon>}
                </Icon>
              </Tooltip>
            </Link>
          ) : null}

          <PwaInstallButton />

          {isMounted && !isLoading && user ? (
            <MenuWrap ref={menuRightRef}>
              <Burger
                opened={showRightMenu}
                onClick={() => setShowRightMenu(!showRightMenu)}
                color={colors.brown}
              />
              {showRightMenu ? (
                <LinksMenu>
                  {user.roles?.some((role) => ['superadmin', 'admin'].includes(role)) ? (
                    <Link href="/konzola">
                      <Button
                        naked
                        justify="flex-start"
                        width={'154px'}
                        color="red"
                        onClick={() => setShowRightMenu(false)}
                      >
                        Konzola
                      </Button>
                    </Link>
                  ) : null}
                  {user.roles?.some((role) =>
                    ['superadmin', 'admin', 'lecturer', 'editor'].includes(role)
                  ) ? (
                    <Link href="/admin">
                      <Button
                        naked
                        justify="flex-start"
                        width={'154px'}
                        color="lagoon"
                        onClick={() => setShowRightMenu(false)}
                      >
                        CMS
                      </Button>
                    </Link>
                  ) : null}
                  <div>
                    <Button
                      naked
                      justify="flex-start"
                      width="154px"
                      onClick={() => {
                        handleLogout();
                        setShowRightMenu(false);
                      }}
                    >
                      Odjavi se
                    </Button>
                  </div>
                </LinksMenu>
              ) : null}
            </MenuWrap>
          ) : null}
        </Section>
        <Mobile>
          {logoIsVisible ? (
            <Link href="/">
              <Image src={izvrsnostLogo} height={40} alt="Inkubator izvrsnosti logo" />
            </Link>
          ) : null}

          <div style={{ display: 'flex', gap: '16px', marginLeft: 'auto', alignItems: 'center' }}>
            {pathname !== '/kalendar' ? (
              <Link href="/kalendar">
                <Tooltip
                  label="Pogledaj raspored edukacija"
                  events={{ hover: true, focus: true }}
                  withArrow
                  transitionProps={{ duration: 200 }}
                  zIndex={100}
                >
                  <Icon>
                    <IconCalendarMonthFilled size={32} stroke={1} color={colors.brown} />
                    <SubIcon>4</SubIcon>
                  </Icon>
                </Tooltip>
              </Link>
            ) : null}
            <PwaInstallButton />
            <Burger
              opened={showMobileMenu}
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              size="md"
              lineSize={2}
              ref={menuMobileRef}
              color={colors.brown}
            />
          </div>
          <MobileMenu
            opened={showMobileMenu}
            pathname={pathname}
            user={user}
            handleLogIn={handleLogIn}
            handleLogout={handleLogout}
          />
        </Mobile>
      </Container>
    </HeaderWrapper>
  );
};

export { Header };
